<!-- Media Accreditation Users -->
<template>
    <div>
        <!--Back Button-->
        <div class="d-flex">
            <app-btn @click.native="handleBack" class="mr-4" color="white" icon="arrowBack" icon-color="primary" />

            <!--Header-->
            <page-title
                icon="accreditation"
                info="Create and manage your Accreditation Users."
                :title="'Accreditation' + (fullEventData?.eventData?.eventName ? ' - ' + fullEventData.eventData.eventName : '')"
            />
        </div>

        <v-divider class="mt-2" />

        <!--Passes Figures-->
        <div
            v-if="fullEventData?.accreditationEventData?.entityId"
            class="mt-4"
            style="display: grid; grid-template-columns: repeat(5, 1fr); grid-gap: 16px"
        >
            <!--Requested-->
            <div class="d-flex flex-column align-center black rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesRequested }}</app-text>
                <app-text color="white" size="small">Requested</app-text>
            </div>

            <!--Approved-->
            <div class="d-flex flex-column align-center green rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesApproved }}</app-text>
                <app-text color="white" size="small">Approved</app-text>
            </div>

            <!--Pending-->
            <div class="d-flex flex-column align-center orange rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesPending }}</app-text>
                <app-text color="white" size="small">Pending</app-text>
            </div>

            <!--Rejected-->
            <div class="d-flex flex-column align-center red rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesRejected }}</app-text>
                <app-text color="white" size="small">Rejected</app-text>
            </div>

            <!--Reserved-->
            <div class="d-flex flex-column align-center blue rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesReserved }}</app-text>
                <app-text color="white" size="small">Reserved</app-text>
            </div>
        </div>

        <!--Action Bar-->
        <div class="d-flex align-center mt-4">
            <!--Search-->
            <app-form-field
                form-type="textInput"
                append-icon="icons8-search"
                class="mr-4"
                :clearable="true"
                label="User Name"
                style="width: 100%"
                v-model.trim="searchByUserName"
            />

            <!--Add Button-->
            <!--<app-btn class="mr-4" icon="add" label="Add"/>-->

            <!--Filter Button - with a numbered badge-->
            <div style="position: relative">
                <app-btn @click.native="toggleFiltersVisibility" class="mr-4" color="appWhite" icon="filter" icon-color="primary" />
                <span v-if="computedNumberOfActiveFilters > 0" class="badge">{{ computedNumberOfActiveFilters }}</span>
            </div>

            <!--More Actions Menu-->
            <more-actions-menu @emitMenuSelection="handleEmittedMenuSelection" :menuList="computedMenuOptions" />
        </div>

        <!--Clear Filters-->
        <div v-if="computedNumberOfActiveFilters > 0" class="d-flex justify-end mt-4">
            <app-btn @click.native="clearFilters" icon="close" label="Clear Filters" />
        </div>

        <!--Getting Started Info-->
        <div
            v-if="!fullEventData?.accreditationEventData?.entityId"
            class="d-flex align-center justify-center"
            :style="$vuetify.breakpoint.width < 600 ? 'height: 50vh' : 'height: 50vh'"
        >
            <page-start-information title="Getting Started" :points="['Configure this event', 'Set it to Live']" />
        </div>

        <!--Table-->
        <v-data-table
            v-if="$vuetify.breakpoint.width >= 600 && fullEventData?.accreditationEventData?.entityId"
            class="appWhite rounded-lg mt-4"
            :headers="computedHeaders"
            :items="computedTableData"
        >
            <!--ID-->
            <template v-slot:item.entityId="{ item }">
                <app-text size="small">{{ item.entityId }}</app-text>
            </template>

            <!--Status-->
            <template v-slot:item.status="{ item }">
                <app-text v-if="!item.passData?.accreditationPassStatus">-</app-text>
                <app-icon
                    v-else
                    :color="getStatusIconConfig(item.status).color"
                    :icon="getStatusIconConfig(item.status).icon"
                    size="32"
                    :title="item.status"
                />
            </template>

            <!--Is Printed-->
            <template v-slot:item.printed="{ item }">
                <app-icon :color="item.printed ? 'green' : 'greyD'" icon="print" size="32" :title="item.printed ? 'Printed' : 'Not Printed'" />
            </template>

            <!--UserName-->
            <template v-slot:item.userName="{ item }">
                <app-text size="small">{{ item.userName }}</app-text>
            </template>

            <!--UserOrganisation-->
            <template v-slot:item.userOrganisation="{ item }">
                <app-text size="small">{{ item.userOrganisation }}</app-text>
            </template>

            <!--UserPosition-->
            <template v-slot:item.userPosition="{ item }">
                <app-text size="small">{{ item.userPosition }}</app-text>
            </template>

            <!--UserPressType-->
            <template v-slot:item.userPressType="{ item }">
                <app-text size="small">{{ item.userPressType }}</app-text>
            </template>

            <!--UserHolderType-->
            <template v-slot:item.userHolderType="{ item }">
                <app-text size="small">{{ item.userHolderType }}</app-text>
            </template>

            <!--Action Button-->
            <template v-slot:item.action="{ item }">
                <app-icon @click.native="handleUserSelection(item)" class="cursorPointer" color="primary" icon="arrowForward" />
            </template>
        </v-data-table>

        <!--Mobile Cards-->
        <div v-if="$vuetify.breakpoint.width < 600">
            <!--No data message-->
            <app-text v-if="!computedPaginatedData.length" class="text-center mt-4" color="grey9">You have no Accreditation Events to view </app-text>

            <!--Cards-->
            <div
                v-for="item in computedPaginatedData"
                :key="item.passData.entityId"
                @click="handleUserSelection(item)"
                class="appWhite d-flex align-center rounded-lg mt-4 pa-4"
            >
                <!--Status and Print Icons-->
                <div class="d-flex flex-column justify-space-around mr-4" style="border-right: 1px solid #dddddd">
                    <!--Status Icons-->
                    <div class="mr-4 mb-4">
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Approved'"
                            color="green"
                            icon="success"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Pending'"
                            color="orange"
                            icon="pending"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Rejected'"
                            color="red"
                            icon="cancel"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Reserved'"
                            color="blue"
                            icon="reserved"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                    </div>

                    <!--Printed Icons-->
                    <div class="mt-4">
                        <app-icon
                            :color="item.passData?.accreditationPassPrinted ? 'green' : 'greyD'"
                            icon="print"
                            size="32"
                            :title="item.passData?.accreditationPassPrinted ? 'Printed' : 'Not Printed'"
                        />
                    </div>
                </div>

                <!--User Details-->
                <div style="width: 100%">
                    <app-text color="primary" size="normal-bold">{{ item.userData.userName }}</app-text>
                    <app-text color="grey9" size="small">{{ item.userData.userPosition }}</app-text>
                    <app-text size="small">{{ item.userOrganisation }}</app-text>

                    <v-divider class="my-2" />

                    <app-text color="grey9" size="small">{{ item.userData.userOutletPublication }}</app-text>
                    <div class="d-flex">
                        <app-text color="grey9" size="small">{{ item.userData.userPressType }}</app-text>
                        <app-text class="mx-2" color="greyD" size="small">|</app-text>
                        <app-text color="grey9" size="small">{{ item.userData.userHolderType }}</app-text>
                    </div>
                </div>
            </div>

            <!--Pagination Controls-->
            <div class="d-flex align-center justify-center mt-4">
                <app-btn @click.native="prevPage" :disabled="currentPage === 1" label="Previous" />
                <app-text class="mx-2">{{ currentPage }} / {{ computedTotalPages }}</app-text>
                <app-btn @click.native="nextPage" :disabled="currentPage === computedTotalPages" label="Next" />
            </div>
        </div>

        <!--Filters Panel ------------------------------------------------------------------------------------------ -->
        <filter-panel :is-filters-panel-visible="isFiltersPanelVisible" @toggle="toggleFiltersVisibility">
            <div class="pa-4">
                <!--Statuses-->
                <page-break-title title="Statuses" />
                <div class="mt-4" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); grid-gap: 16px">
                    <app-btn
                        @click.native="handleFilterStatusSelection('Pending')"
                        :block="true"
                        color="orange"
                        label="Pending"
                        :outlined="!filterByStatuses.includes('Pending')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Approved')"
                        :block="true"
                        color="green"
                        label="Approved"
                        :outlined="!filterByStatuses.includes('Approved')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Rejected')"
                        :block="true"
                        color="red"
                        label="Rejected"
                        :outlined="!filterByStatuses.includes('Rejected')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Reserved')"
                        :block="true"
                        color="blue"
                        label="Reserved"
                        :outlined="!filterByStatuses.includes('Reserved')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Not Applied')"
                        :block="true"
                        color="grey"
                        label="Not Applied"
                        :outlined="!filterByStatuses.includes('Not Applied')"
                    />
                </div>

                <!--Is Printed-->
                <page-break-title class="mt-8" title="Print Status" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="['Printed', 'Not Printed']"
                        label="Print Status"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByPrintStatus"
                    />
                </div>

                <!--Position-->
                <page-break-title class="mt-8" title="Position" />
                <div class="mt-4">
                    <app-form-field form-type="textInput" label="Position" v-model.trim="filterByPosition" />
                </div>

                <!--Press Types-->
                <page-break-title class="mt-8" title="Press Types" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="computedPressTypeOptions"
                        label="Press Types"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByPressTypes"
                    />
                </div>

                <!--Holder Types-->
                <page-break-title class="mt-8" title="Holder Types" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="computedHolderTypeOptions"
                        label="Holder Types"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByHolderTypes"
                    />
                </div>
            </div>
        </filter-panel>
    </div>
</template>

<script>
export default {
    name: 'MediaAccreditationUsers',

    components: {},

    props: ['fullEventData'],

    data: () => ({
        isFiltersPanelVisible: false,
        filterByHolderTypes: [],
        filterByPressTypes: [],
        filterByPosition: '',
        filterByPrintStatus: [],
        filterByStatuses: [],
        isLoading: false,
        isReadOnly: false,
        searchByUserName: '',
        selectedItem: {},
        tableHeaders: [
            { text: 'ID', value: 'entityId', align: 'left', sortable: false, hidden: true },
            { text: 'Status', value: 'status', align: 'center', sortable: false, width: '48px' }, // Changed
            { text: 'Printed?', value: 'printed', align: 'center', sortable: false, width: '48px' }, // Changed
            { text: 'Name', value: 'userName', align: 'start', sortable: false },
            { text: 'Organisation', value: 'userOrganisation', align: 'start', sortable: false },
            { text: 'Position', value: 'userPosition', align: 'start', sortable: false },
            { text: 'Press Type', value: 'userPressType', align: 'start', sortable: false },
            { text: 'Holder Type', value: 'userHolderType', align: 'start', sortable: false },
            { text: '', value: 'action', align: 'right', sortable: false, width: '48px' }
        ],

        // Pagination
        currentPage: 1,
        itemsPerPage: 10
    }),

    computed: {
        /**
         * Computed Export CSV
         *
         * Return the data and headers for the CSV export
         *
         * @returns {{headers: {}, data: *[]}}
         */
        computedExportCSV() {
            const t = this
            let data = []
            let headers = {}
            const FULL_EVENT_DATA = t.$props.fullEventData

            // Add the readable headers for the CSV columns
            headers = {
                entityId: 'ID',
                eventName: 'Event Name',
                eventDate: 'Event Date',
                accreditationPassStatus: 'Pass Status',
                accreditationPassPrinted: 'Print Status',
                accreditationPassUserId: 'User Name',
                accreditationPassEmail: 'User Email',
                accreditationPassOrganisationId: 'Organisation',
                userPosition: 'User Job Title',
                userAsMediaMediaType: 'Media Type',
                userAsMediaHolderType: 'Holder Type'
            }

            // Add the data
            t.computedTableData.forEach((entry) => {
                const DATA_OBJECT = {
                    entityId: entry?.passData?.entityId || '',
                    eventName: FULL_EVENT_DATA?.eventData?.eventName || '',
                    eventDate: t.MIX_formatDate(FULL_EVENT_DATA?.eventData?.eventDate, 'numeric') || '',
                    accreditationPassStatus: entry?.status || '',
                    accreditationPassPrinted: entry?.printed ? 'Printed' : 'Not Printed',
                    accreditationPassUserId: entry?.userName || '',
                    accreditationPassEmail: entry?.userEmail || '',
                    accreditationPassOrganisationId: entry?.userOrganisation || '',
                    userPosition: entry?.userPosition || '',
                    userAsMediaMediaType: entry?.userPressType || '',
                    userAsMediaHolderType: entry?.userHolderType || ''
                }

                data.push(DATA_OBJECT)
            })

            return { headers, data }
        },

        /**
         * Computed Headers
         *
         * Remove hidden headers and only show the actions column to those with access.
         *
         * @returns an array of header objects
         */
        computedHeaders() {
            const t = this
            let headers = t.tableHeaders

            // Remove hidden headers
            headers = headers.filter((h) => !h.hidden)

            return headers
        },

        /**
         * Computed Menu Options
         *
         * Return the menu options based on the event status.
         *
         * @returns {*[]} an array of menu options
         */
        computedMenuOptions() {
            const t = this
            let menuOptions = []

            menuOptions.push({ name: 'Event Settings', icon: 'settings' })
            menuOptions.push({ name: 'Export', icon: 'export' })

            // Only show these options if the event is live
            // if (t.$props.fullEventData?.accreditationEventData?.entityId) {
            // 	menuOptions.push({name: 'Print Manager', icon: 'print'})
            // }

            return menuOptions
        },

        /**
         * Computed Number of Active Filters
         *
         * Count the number of active filters to display in the filters button badge.
         *
         * @returns {*}
         */
        computedNumberOfActiveFilters() {
            const t = this
            return [
                t.filterByHolderTypes.length,
                t.filterByPressTypes.length,
                t.filterByPosition ? 1 : 0,
                t.filterByPrintStatus.length,
                t.filterByStatuses.length
            ].reduce((acc, curr) => acc + curr, 0)
        },

        /**
         * Computed Paginated Data
         *
         * Return the paginated data for the mobile view.
         * The data is sliced based on the current page and items per page.
         *
         * Note: This is only used for the mobile view, and is required so the mobile view does crash when the data is too large.
         *
         * @returns {array[{}]} an array of objects
         */
        computedPaginatedData() {
            const t = this

            const START_PAGE = (t.currentPage - 1) * t.itemsPerPage
            const END_PAGE = START_PAGE + t.itemsPerPage

            return t.computedTableData.slice(START_PAGE, END_PAGE)
        },

        /**
         * Computed Table Data
         *
         * Build the table rows from the Event's Passes data, then apply the search and every filter.
         *
         * The server now returns one entry per applicant for the whole Event, with their most recent Pass
         * and their resolved Organisation names, so there is no per-Organisation reconstruction or
         * de-duplication to do here.
         *
         * @returns {array[{}]} an array of objects
         */
        computedTableData() {
            const t = this
            let tableData = []

            const PASSES_DATA = t.$props.fullEventData?.passesData || []

            PASSES_DATA.forEach((item) => {
                const USER_DATA = item.userData || {}
                const PASS_DATA = item.passData || {}
                const ORGANISATION_NAMES = item.organisationNames || []

                tableData.push({
                    entityId: USER_DATA.entityId,
                    status: PASS_DATA.accreditationPassStatus || 'Not Applied',
                    printed: PASS_DATA.accreditationPassPrinted || false,
                    userName: USER_DATA.userName || 'Unknown User',
                    userEmail: USER_DATA.userEmail || '',
                    userOrganisation: ORGANISATION_NAMES.join(', '),
                    userPosition: USER_DATA.userPosition || '',
                    userPressType: USER_DATA.userPressType || '',
                    userHolderType: PASS_DATA.accreditationPassHolderType || USER_DATA.userHolderType || '',
                    organisationNames: ORGANISATION_NAMES,
                    organisationData: item.organisationData || { entityId: '', organisationName: ORGANISATION_NAMES.join(', ') },
                    userData: USER_DATA,
                    passData: PASS_DATA
                })
            })

            // Apply search filter
            if (t.searchByUserName) {
                const SEARCH_TERM = t.searchByUserName.toUpperCase()
                tableData = tableData.filter((item) => item.userName.toUpperCase().includes(SEARCH_TERM))
            }

            // Apply status filters
            if (t.filterByStatuses.length) {
                tableData = tableData.filter((item) => t.filterByStatuses.includes(item.status))
            }

            // Apply print status filters
            if (t.filterByPrintStatus.length) {
                tableData = tableData.filter((item) => t.filterByPrintStatus.includes(item.printed ? 'Printed' : 'Not Printed'))
            }

            // Apply position filter
            if (t.filterByPosition) {
                const POSITION = t.filterByPosition.toUpperCase()
                tableData = tableData.filter((item) => item.userPosition.toUpperCase().includes(POSITION))
            }

            // Apply press type filters
            if (t.filterByPressTypes.length) {
                tableData = tableData.filter((item) => t.filterByPressTypes.includes(item.userPressType))
            }

            // Apply holder type filters
            if (t.filterByHolderTypes.length) {
                tableData = tableData.filter((item) => t.filterByHolderTypes.includes(item.userHolderType))
            }

            return tableData
        },

        /**
         * Computed Press Type Options
         *
         * Return the Press Types actually present on this Event, for the filter dropdown.
         *
         * @returns {string[]} a sorted array of Press Types
         */
        computedPressTypeOptions() {
            const t = this
            const OPTIONS = new Set()

            ;(t.$props.fullEventData?.passesData || []).forEach((item) => {
                if (item.userData?.userPressType) OPTIONS.add(item.userData.userPressType)
            })

            return [...OPTIONS].sort()
        },

        /**
         * Computed Holder Type Options
         *
         * Return the Holder Types actually present on this Event, for the filter dropdown.
         *
         * @returns {string[]} a sorted array of Holder Types
         */
        computedHolderTypeOptions() {
            const t = this
            const OPTIONS = new Set()

            ;(t.$props.fullEventData?.passesData || []).forEach((item) => {
                const HOLDER_TYPE = item.passData?.accreditationPassHolderType || item.userData?.userHolderType
                if (HOLDER_TYPE) OPTIONS.add(HOLDER_TYPE)
            })

            return [...OPTIONS].sort()
        },

        /**
         * Computed Status Counts
         *
         * Count the Passes by status across the whole Event, so the figures along the top stay a summary of
         * the Event rather than of whatever filter happens to be applied. They double as a cross-check
         * against a status-filtered export.
         *
         * @returns {{}} an object of status counts
         */
        computedStatusCounts() {
            const t = this
            const COUNTS = { Pending: 0, Approved: 0, Rejected: 0, Reserved: 0, requested: 0 }

            ;(t.$props.fullEventData?.passesData || []).forEach((item) => {
                const STATUS = item.passData?.accreditationPassStatus

                if (['Pending', 'Approved', 'Rejected', 'Reserved'].includes(STATUS)) {
                    COUNTS[STATUS] += 1
                    COUNTS.requested += 1
                }
            })

            return COUNTS
        },

        numberOfPassesRequested() {
            return this.computedStatusCounts.requested
        },

        numberOfPassesApproved() {
            return this.computedStatusCounts.Approved
        },

        numberOfPassesPending() {
            return this.computedStatusCounts.Pending
        },

        numberOfPassesRejected() {
            return this.computedStatusCounts.Rejected
        },

        numberOfPassesReserved() {
            return this.computedStatusCounts.Reserved
        },

        /**
         * Computed Current Page
         *
         * Return the current page number.
         *
         * @returns {number} - the current page number
         */
        computedTotalPages() {
            return Math.ceil(this.computedTableData.length / this.itemsPerPage)
        }
    },

    methods: {
        /**
         * Clear Filters
         *
         * Clear all the filters.
         */
        clearFilters() {
            const t = this

            t.filterByHolderTypes = []
            t.filterByPressTypes = []
            t.filterByPosition = ''
            t.filterByPrintStatus = []
            t.filterByStatuses = []
        },

        /**
         * Export Data
         *
         * Export the data to CSV.
         */
        exportData() {
            const t = this

            t.MIX_exportDocuments(t.computedExportCSV.headers, 'Accreditation', t.computedExportCSV.data)
        },

        /**
         * Get Status Icon Config
         *
         * Return the icon and color for the status.
         *
         * @param status {string} the status
         * @returns {{color: string, icon: string}}
         */
        getStatusIconConfig(status) {
            let icon = ''
            let color = ''

            if (status === 'Pending') {
                icon = 'pending'
                color = 'orange'
            }

            if (status === 'Approved') {
                icon = 'success'
                color = 'green'
            }

            if (status === 'Rejected') {
                icon = 'cancel'
                color = 'red'
            }

            if (status === 'Reserved') {
                icon = 'reserved'
                color = 'blue'
            }

            return { icon, color }
        },

        /**
         * Handle Emitted Menu Selection
         *
         * Take the emitted selection from the menu, and call the relevant function.
         *
         * @param selection a string-based menu option
         */
        handleEmittedMenuSelection(selection) {
            const t = this

            if (selection === 'Event Settings') t.$emit('openEventSettings', t.$props.fullEventData)
            if (selection === 'Export') t.exportData()
            if (selection === 'Print Manager') t.$emit('openPrintManager', t.$props.fullEventData)
        },

        /**
         * Handle Filter Status Selection
         *
         * Add or remove the selected status from the filterStatuses array.
         *
         * @param status {string} the status to add or remove
         */
        handleFilterStatusSelection(status) {
            const t = this

            if (t.filterByStatuses.includes(status)) t.filterByStatuses = t.filterByStatuses.filter((s) => s !== status)
            else t.filterByStatuses.push(status)
        },

        /**
         * Handle User Selection
         *
         * Emit the selected user to the parent for saving
         * .
         * @param item {object} the selected user
         */
        handleUserSelection(item) {
            const t = this

            t.selectedItem = item

            t.$emit('selectedUser', { selectedUser: item, formType: 'PassForm' })
        },

        /**
         * Next Page
         *
         * Go to the next page.
         */
        nextPage() {
            const t = this

            if (t.currentPage < t.computedTotalPages) t.currentPage += 1
        },

        /**
         * Prev Page
         *
         * Go to the previous page.
         */
        prevPage() {
            const t = this

            if (t.currentPage > 1) t.currentPage -= 1
        },

        /**
         * Toggle Filters Visibility
         *
         * Toggle the visibility of the filters panel.
         */
        toggleFiltersVisibility() {
            const t = this

            t.isFiltersPanelVisible = !t.isFiltersPanelVisible
        },

        /**
         * Handle Back
         *
         * Navigate back to organisations view
         */
        handleBack() {
            this.$emit('backToEvents')
        }
    }
}
</script>

<style scoped></style>
